import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import {
  clinicInfoPatchSchema,
  clinicProfileResponseSchema,
  workingHoursPayloadSchema,
  workingHoursResponseSchema,
  type ClinicInfo,
  type ClinicProfileResponse,
  type WorkingHoursPayload,
  type WorkingHoursResponse,
} from './settings-contracts'

export type SettingsRepository = {
  getClinicProfile: (tenantId: string) => Promise<ClinicProfileResponse>
  updateClinicInfo: (
    tenantId: string,
    payload: ClinicInfo
  ) => Promise<ClinicProfileResponse>
  getWorkingHours: (tenantId: string) => Promise<WorkingHoursResponse>
  updateWorkingHours: (
    tenantId: string,
    payload: WorkingHoursPayload
  ) => Promise<WorkingHoursResponse>
}

type RepositoryOptions = {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}

function endpoint(tenantId: string, resource: 'clinic-info' | 'working-hours') {
  return `/dashboard/clinics/${encodeURIComponent(tenantId)}/${resource}`
}

export function createSettingsRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation,
}: RepositoryOptions): SettingsRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })

  return {
    getClinicProfile(tenantId) {
      return client.request(endpoint(tenantId, 'clinic-info'), {
        method: 'GET',
        responseSchema: clinicProfileResponseSchema,
      })
    },
    updateClinicInfo(tenantId, payload) {
      return client.request(endpoint(tenantId, 'clinic-info'), {
        method: 'PATCH',
        body: clinicInfoPatchSchema.parse(payload),
        responseSchema: clinicProfileResponseSchema,
      })
    },
    getWorkingHours(tenantId) {
      return client.request(endpoint(tenantId, 'working-hours'), {
        method: 'GET',
        responseSchema: workingHoursResponseSchema,
      })
    },
    updateWorkingHours(tenantId, payload) {
      return client.request(endpoint(tenantId, 'working-hours'), {
        method: 'PATCH',
        body: workingHoursPayloadSchema.parse(payload),
        responseSchema: workingHoursResponseSchema,
      })
    },
  }
}

export function isSettingsPermissionError(error: unknown) {
  return (
    error instanceof ApiError &&
    (error.status === 401 ||
      error.status === 403 ||
      error.code === 'unauthenticated')
  )
}

export function isPermissionDenied(error: unknown) {
  return isSettingsPermissionError(error)
}
